import { useState, useRef } from "react"
import PropTypes from "prop-types"
import { capitalize } from "humanize-plus"

import classnames from "classnames"

import StepConfig from "./step_config"
import CollapsedConfigItem from "./collapsed_config_item"
import Modal from "shared/components/modal"
import CustomFields from "./custom_fields"
import Form from "app/components/sdk/form"
import LimitedTextInput from "shared/limited_text_input"
import InlineErrors from "shared/components/inline_errors"

import { customFieldShape } from "partners/components/shared/shared_prop_types"

import {
  pluralizeStepType,
  arrayAdd,
  arrayRemove,
  arrayReplace,
} from "shared/lib/utils"

import styles from "./applet_configuration.module.scss"

const AppletConfiguration = ({
  config,
  contextChannelId,
  onConfigChange,
  onConfigRemove,
  oneBasedIndex,
  availableTandas,
  customFieldNames,
  errors,
  published,
  icons,
  appletUpdating,
  docs_url,
  display,
  firstNotHidden,
}) => {
  const [configViewOpen, updateConfigView] = useState(false)
  const [title, updateTitle] = useState(config.title)
  const [description, updateDescription] = useState(config.description)
  const [required, updateRequired] = useState(config.required)
  const [icon, updateIcon] = useState(config.icon || icons[0])
  const [customFields, updateCustomFields] = useState(config.customFields)

  const handleRequiredChange = e => {
    updateRequired(e.target.value === "true")
  }

  const onUpdateConfig = () => {
    updateConfigView(false)
    onConfigChange({
      ...config,
      title,
      description,
      icon,
      required,
      customFields: customFields.map(collapseItem),
      triggers: triggers.map(collapseItem),
      queries: queries.map(collapseItem),
      actions: actions.map(collapseItem),
    })
    updateAddButtonTriggerDisableStatus(false)
    updateAddButtonQueryDisableStatus(false)
    updateAddButtonActionDisableStatus(false)
  }

  const onModalClose = () => {
    updateConfigView(false)
    updateAddButtonTriggerDisableStatus(false)
    updateAddButtonQueryDisableStatus(false)
    updateAddButtonActionDisableStatus(false)

    // Reset the local state
    updateTitle(config.title)
    updateDescription(config.description)
    updateIcon(config.icon)
    updateRequired(config.required)
    updateCustomFields(config.customFields.map(collapseItem))
    updateTriggers(config.triggers.map(collapseItem))
    updateQueries(config.queries.map(collapseItem))
    updateActions(config.actions.map(collapseItem))
  }

  const collapseItem = item => {
    item.collapsed = true
    return item
  }

  const isTandaAdditionAllowed = type => {
    return availableTandas[pluralizeStepType(type)].length > 0
  }

  const [triggers, updateTriggers] = useState(config.triggers)
  const [queries, updateQueries] = useState(config.queries)
  const [actions, updateActions] = useState(config.actions)
  const getSteps = type => {
    switch (type) {
      case "trigger":
        return triggers
      case "query":
        return queries
      case "action":
        return actions
    }
  }
  const updateSteps = type => {
    switch (type) {
      case "trigger":
        return updateTriggers
      case "query":
        return updateQueries
      case "action":
        return updateActions
    }
  }

  const handleAddStep = type => {
    if (isTandaAdditionAllowed(type)) {
      updateSteps(type)(
        arrayAdd(getSteps(type), {
          type,
          channel_module_name: "",
          module_name: "",
          fields: [],
          collapsed: false,
        })
      )

      updateAddButtonDisableStatus(type)(true)
    }
  }

  const handleRemoveStep = (type, index) => {
    return () => {
      updateSteps(type)(arrayRemove(getSteps(type), index))

      if (!getSteps(type)[index].collapsed) {
        updateAddButtonDisableStatus(type)(false)
      }
    }
  }

  const handleUpdateStep = (type, index) => {
    return updatedProps => {
      const step = getSteps(type)[index]

      updateSteps(type)(
        arrayReplace(getSteps(type), index, {
          ...step,
          ...updatedProps,
        })
      )
    }
  }

  const handleUpdateStepField = (type, stepIndex) => {
    return (fieldIndex, stepField, updatedProps) => {
      const step = getSteps(type)[stepIndex]
      const fields = arrayReplace(step.fields, fieldIndex, {
        ...stepField,
        ...updatedProps,
      })

      handleUpdateStep(type, stepIndex)({ fields })
    }
  }

  const handleStepSelect = index => {
    return step => handleUpdateStep(step.type, index)(step)
  }

  const handleUpdateTandaCollapse = index => {
    return (type, collapsed) => {
      handleUpdateStep(type, index)({ collapsed })
      updateAddButtonDisableStatus(type)(!collapsed)
    }
  }

  const iconToString = icon => capitalize(icon).replace("_", " ")
  const featureHeaderRef = {
    action: useRef(null),
    query: useRef(null),
    trigger: useRef(null),
  }

  const [
    addButtonTriggerDisableStatus,
    updateAddButtonTriggerDisableStatus,
  ] = useState(false)
  const [
    addButtonQueryDisableStatus,
    updateAddButtonQueryDisableStatus,
  ] = useState(false)
  const [
    addButtonActionDisableStatus,
    updateAddButtonActionDisableStatus,
  ] = useState(false)
  const updateAddButtonDisableStatus = step => {
    switch (step) {
      case "trigger":
        return updateAddButtonTriggerDisableStatus
      case "query":
        return updateAddButtonQueryDisableStatus
      case "action":
        return updateAddButtonActionDisableStatus
    }
  }
  const addButtonDisableStatus = step => {
    switch (step) {
      case "trigger":
        return addButtonTriggerDisableStatus
      case "query":
        return addButtonQueryDisableStatus
      case "action":
        return addButtonActionDisableStatus
    }
  }

  return (
    <div
      className={classnames(
        styles["config-item-container"],
        display ? styles.visible : styles.hidden,
        { [styles["visible-after-hidden"]]: firstNotHidden === oneBasedIndex }
      )}
    >
      <CollapsedConfigItem
        type="feature"
        status={Object.keys(errors.all).length ? "dirty" : config.status}
        title={config.title}
        index={oneBasedIndex}
        onUpdateViewClick={updateConfigView}
        onRemoveClick={onConfigRemove}
        appletUpdating={appletUpdating}
      />
      <Modal show={configViewOpen} includeCloseX={false} fullscreen>
        <div className={styles["big-modal-container"]}>
          <div className={styles["feature-header"]}>
            <div className={styles["header-flex-item"]}>
              <button
                className="button-tertiary button-outlined"
                onClick={onModalClose}
              >
                Cancel
              </button>
            </div>
            <div className={styles["header-flex-item"]}>
              <p>Feature Settings</p>
            </div>
            <div className={styles["header-flex-item"]} />
          </div>
          <div className={styles["form-content"]}>
            <div className={styles["non-step-fields"]}>
              <Form
                onSubmit={e => {
                  {
                    /* this onSubmit handler isn't working as expected, it never runs. It does insert an <input/> in the dom and prevents a differnt form handler from being run on Enter  */
                  }
                  e.preventDefault()
                }}
              >
                <div className={classnames("label", styles["align-left"], styles.label)}>
                  Feature title
                </div>
                <InlineErrors errors={errors.getAll("title")} />
                <div className="field">
                  <LimitedTextInput
                    value={title}
                    onChange={updateTitle}
                    limit={100}
                    multiline={false}
                    counterPosition="top"
                    hardLimit
                  />
                </div>
                <div className={classnames("label", styles["align-left"], styles.label)}>
                  Feature description
                </div>
                <LimitedTextInput
                  value={description}
                  onChange={updateDescription}
                  limit={500}
                  minRows={3}
                  counterPosition="top"
                  errors={errors.getAll("description")}
                  multiline
                  hardLimit
                />
              </Form>

              <div className={classnames(styles.select, styles["required-config"])}>
                <div className="input">
                  <select value={required} onChange={handleRequiredChange}>
                    <option key="required-false" value={false}>
                      This feature is optional for users
                    </option>
                    <option key="required-true" value={true}>
                      This feature is required for users
                    </option>
                  </select>
                </div>
              </div>

              <div className={classnames("field", styles["icon-row"])}>
                <div className={styles.icon}>
                  <img
                    src={`/value-prop-icons/${icon}.svg`}
                    title={iconToString(icon)}
                    alt={iconToString(icon)}
                    height={40}
                    width={40}
                  />
                </div>
                <div className={styles.select}>
                  <div className="input">
                    <select
                      value={icon}
                      onChange={e => updateIcon(e.target.value)}
                    >
                      {icons.map(icon => (
                        <option key={icon} value={icon}>
                          {iconToString(icon)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <p>
                Icon should relate the title of the feature. ex: If it has
                something to do with scheduling, you could use a clock.
              </p>
              <h4>Options</h4>
              <p>
                Options allow you to capture additional information from your
                users to power this feature.
              </p>
              <CustomFields
                customFields={customFields}
                disabled={false}
                onCustomFieldsChange={updateCustomFields}
                errors={errors}
                configUI
                customFieldNames={customFieldNames}
              />
            </div>
            <span className={classnames(styles["horizontal-rule"], styles.space)} />

            <h1 className={styles["align-left"]}>Feature configurations</h1>

            <p>
              Select the triggers, queries, and actions that are required for
              the experience you're looking to create. Learn more by{" "}
              <a
                className="link-inline"
                href={`${docs_url}#tips-on-configuration`}
                target="_blank"
                rel="noopener noreferrer"
              >
                visiting our documentation
              </a>
              .
            </p>

            {["trigger", "query", "action"].map(stepName => (
              <div className={styles["step-config-section"]} key={stepName}>
                <div className={styles["left-button"]}>
                  <button
                    className={classnames(
                      "button-tertiary",
                      { "button-outlined": isTandaAdditionAllowed(stepName) }
                    )}
                    onClick={() => handleAddStep(stepName)}
                    disabled={
                      addButtonDisableStatus(stepName) ||
                      !isTandaAdditionAllowed(stepName)
                    }
                    ref={featureHeaderRef[stepName]}
                  >
                    Add {stepName}
                  </button>
                  <span className={styles["horizontal-rule"]} />
                </div>
                {getSteps(stepName).map((step, i) => (
                  <StepConfig
                    contextChannelId={contextChannelId}
                    step={{ ...step, indexByType: step.indexByType || i }}
                    availableTandas={availableTandas}
                    alreadySelectedTandas={getSteps(stepName).map(
                      s => s.module_name
                    )}
                    key={i}
                    oneBasedIndex={i + 1}
                    onStepRemove={handleRemoveStep(stepName, i)}
                    onUpdateField={handleUpdateStepField(stepName, i)}
                    onSelectStep={handleStepSelect(i)}
                    onChangeCollapse={handleUpdateTandaCollapse(i)}
                    published={published}
                    errors={errors}
                    refToScroll={featureHeaderRef[stepName]}
                  />
                ))}
              </div>
            ))}
            <span className={classnames(styles["horizontal-rule"], styles.space)} />
            <div className={styles["right-button"]}>
              <button className="button-primary" onClick={onUpdateConfig}>
                Save and close
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
export const appletConfigShape = PropTypes.shape({
  description: PropTypes.string,
  icon: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  required: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      module_name: PropTypes.string,
      channel_module_name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.object),
      indexByType: PropTypes.number,
    })
  ),
  queries: PropTypes.arrayOf(
    PropTypes.shape({
      module_name: PropTypes.string,
      channel_module_name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.object),
      indexByType: PropTypes.number,
    })
  ),
  triggers: PropTypes.arrayOf(
    PropTypes.shape({
      module_name: PropTypes.string,
      channel_module_name: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.object),
      indexByType: PropTypes.number,
    })
  ),
  customFields: PropTypes.arrayOf(customFieldShape),
})

AppletConfiguration.propTypes = {
  config: appletConfigShape.isRequired,
  contextChannelId: PropTypes.string.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  onConfigRemove: PropTypes.func.isRequired,
  oneBasedIndex: PropTypes.number.isRequired,
  availableTandas: PropTypes.object.isRequired,
  customFieldNames: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  published: PropTypes.bool.isRequired,
  icons: PropTypes.array.isRequired,
  appletUpdating: PropTypes.bool.isRequired,
  docs_url: PropTypes.string.isRequired,
  display: PropTypes.bool.isRequired,
  firstNotHidden: PropTypes.number.isRequired,
}

export default AppletConfiguration
